'use client';

import { useAuthContext } from '@/contexts/auth-context';
const ACTIVE_SUBSCRIPTION_STATUS = 'active';

/**
 * User data hook with convenient computed properties.
 *
 * Provides:
 * - Authentication state (isAuthenticated, isLoading)
 * - Combined user object from Supabase Auth + backend
 * - Computed getters for common user data:
 *   - userId, userEmail, userPlan
 *   - hasActiveSubscription, isPaidUser
 *   - stripeCustomerId, stripeProductId
 *
 * Use this hook when you need:
 * - Quick access to user properties (userId, userEmail, etc.)
 * - Subscription/plan status checks (isPaidUser, hasActiveSubscription)
 *
 * For modal controls or raw user objects, use useAuth instead.
 *
 * @see useAuth for modal controls and raw user objects
 */
export function useAuthUser() {
  const {
    isAuthenticated,
    isLoading,
    user,
    supabaseUser,
    isSupabaseUserLoading,
    isBackendUserResolved,
    activeOrg,
    professionalMfaRequired,
    mfa,
    refreshUserData,
  } = useAuthContext();

  return {
    // Quick access to auth state
    isAuthenticated,
    isLoading: isLoading || isSupabaseUserLoading,

    /** True once the backend /users/me fetch has resolved at least once.
     *  Gates reading backend-derived flags (practiceEnabled, plan, etc.) should
     *  wait for this before treating a false as authoritative — the loading
     *  flags alone leave a pre-fetch window (#6324). */
    isBackendUserResolved,

    // User data
    user, // Combined user object with all the data you need

    // Raw Supabase user for advanced use cases
    supabaseUser,

    // Active organization (null when no org is active)
    activeOrg,

    // Actions
    refreshUserData,

    // Helper getters for common use cases
    get userId() {
      return user?.id;
    },

    get userEmail() {
      return user?.email;
    },

    get userPlan() {
      return user?.plan;
    },

    /**
     * Resolved capabilities map for the caller's current ``plan_tag``,
     * sourced from ``plans.capabilities`` by the backend ``/users/me``
     * endpoint. Empty object when the user has no plan or while the
     * supabaseUser request is in flight. Feature gates read flags from
     * this map (e.g. ``bundle_professional_profile``) — never check
     * plan tags client-side per CLAUDE.md plan-catalog rule.
     */
    get planCapabilities(): Record<string, boolean | number> {
      return supabaseUser?.plan_capabilities ?? {};
    },

    get hasActiveSubscription() {
      return user?.subscriptionStatus === 'active';
    },

    get isPaidUser() {
      // Implement the same logic as middleware
      if (!user?.subscriptionStatus || !user?.currentPeriodEnd) {
        return false;
      }

      return (
        user.subscriptionStatus === ACTIVE_SUBSCRIPTION_STATUS &&
        new Date(user.currentPeriodEnd) > new Date()
      );
    },

    get stripeCustomerId() {
      return user?.stripeCustomerId;
    },

    get stripeProductId() {
      return user?.stripeProductId;
    },

    // Organization getters
    get orgId() {
      return activeOrg?.id;
    },

    get orgName() {
      return activeOrg?.name;
    },

    get orgRole() {
      return activeOrg?.role;
    },

    get isOrgAdmin() {
      // Owner has all admin rights (org:admin removed, org:owner is the new admin)
      return activeOrg?.isAdmin ?? false;
    },

    get isOrgOwner() {
      return activeOrg?.role === 'org:owner';
    },

    get isInOrg() {
      return activeOrg !== null;
    },

    get isProfessional() {
      return user?.isProfessional ?? false;
    },

    /**
     * The SAK MFA verdict stamped on the JWT (`professional_require_mfa`,
     * #13125). Wider than `isProfessional`: it also fires for a confirmed
     * email found in the SAK register and for a self-declared advocate once
     * the rollout grace instant has passed. This is what the middlewares
     * enforce, so MFA UI keys off it, not off the claimed-profile flag.
     */
    get professionalMfaRequired() {
      return professionalMfaRequired;
    },

    /**
     * Latched practice-surface visibility (#6324): the user can open Moja
     * kancelária when they have self-declared as *Advokát*
     * (`practice_self_enabled`, latched) OR have a claimed advocate profile
     * (`isProfessional`). Decoupled from the live `profession` persona, so
     * switching the chat lens to "Občan" never tears down the office. Governs
     * the PRIVATE office only — public/verified features stay behind a SAK
     * claim. See product-structure-proposal.md §3.1–§3.3.
     */
    get practiceEnabled() {
      return (
        (supabaseUser?.practice_self_enabled ?? false) ||
        (user?.isProfessional ?? false)
      );
    },

    get professionalId() {
      return user?.professionalId ?? null;
    },

    // MFA getters
    mfa,

    get isMfaChallengeRequired() {
      return mfa.challengeRequired;
    },

    get isMfaVerified() {
      return mfa.currentLevel === 'aal2';
    },

    get hasMfaEnrolled() {
      return mfa.nextLevel === 'aal2';
    },
  };
}
